import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AiChatServiceService } from "src/app/components/direct/service/ai-chat-service.service";
import { ApiService } from "src/app/services/api.service";
import { DpmStaticContext } from "../dpm-static/dpm-static.service";

// ===========================================================================
// SQL Cache Review — data layer
//
// Everything this screen talks to the outside world about, in one place: the
// read (chat_history rows with their usage detail, api/chat-history/usage/get-detail
// through ApiService) and the write (a reviewer's Approve / Reject, as the
// chat API's cache_review action — see submitReview).
//
// Two APIs on purpose, because the two facts live in different places: the usage
// rows are the portal's read of chat_history, while the verdict is a change to the
// assistant's own cache and is recorded where the assistant records everything
// else about a message.
//
// The queue does not run the SQL it shows. It is read and copied — a reviewer
// takes a query to their own client if they want to execute it — so nothing here
// touches the chat's execute-SQL path.
//
// Kept beside the component rather than in dpm-static.service.ts because none
// of it is shared: DPM and DEM read an extract feed and neither has SQL in it.
// Anything a THIRD static screen needs from here moves up to dashboard/static/ —
// see the layout note in static-dashboards.ts.
// ===========================================================================

/**
 * The five cache states the review queue is built around, in the spelling the
 * screen displays.
 *
 * The feed's own spelling varies — "GOOD(READY FOR REVIEW)" in chat_history,
 * "GOOD(READYFORREVIEW)" in the design reference — so every read goes through
 * canonicalCacheStatus() and these are what it answers with. A status the
 * backend adds later is passed through as its own value instead of being folded
 * into one of these, so a new state shows up as a new tab rather than being
 * miscounted as pending.
 *
 * GOOD/BAD(READY FOR REVIEW) and APPROVED/REJECTED are FOUR states, not two
 * spellings of two. A ready-for-review value is a verdict recorded against the
 * message and waiting to be signed off; APPROVED / REJECTED is the settled
 * reading. The queue used to label GOOD as "Approved" and BAD as "Rejected",
 * which collapsed the pair and left a reviewer unable to see what still needed
 * signing off — so each of the five now counts, tabs and badges as itself.
 *
 * `pending` keeps its internal spelling ("pending") rather than "NO FEEDBACK":
 * it is only ever a lookup key here, the feed writes both forms (see
 * canonicalCacheStatus) and what the screen shows is CACHE_STATUS_META's label.
 */
export const CACHE_STATUS = {
  pending: "pending",
  good: "GOOD(READY FOR REVIEW)",
  bad: "BAD(READY FOR REVIEW)",
  approved: "APPROVED",
  rejected: "REJECTED",
};

/**
 * Tab / badge / dot appearance per status.
 *
 * `dot` doubles as the colour of the KPI figure that counts it, so a tab and its
 * card can never disagree.
 *
 * The names are the column's, in the reviewer's casing: the feed sends five values
 * and each means a different thing to the person working the queue, so the tabs, the
 * badges and the KPI cards name all five. GOOD / BAD (ready for review) are what a
 * verdict WRITES (see groupCacheRows) and read as proposed; APPROVED / REJECTED are
 * the settled readings. Naming GOOD "Approved" would file a query that still needs
 * signing off under the figure counting the ones already signed off.
 *
 * The colours say the same thing: the two ready-for-review states carry muted
 * versions of the settled green and red, so a queue reads as proposed-against-settled
 * at a glance without two more hues to learn. The value as sent is still a hover
 * away, on the detail pane's badge.
 *
 * `label` is the form the badges wear and `short` the form the pill tabs do, which
 * are a segmented control and stay legible only while each segment is short — which
 * is where the pair stops coinciding: "Good (ready for review)" is a badge, "Good"
 * is the pill.
 */
export const CACHE_STATUS_META: Record<
  string,
  { label: string; short: string; badge: string; dot: string }
> = {
  [CACHE_STATUS.pending]: {
    label: "No feedback",
    short: "No feedback",
    badge: "badge-neutral",
    dot: "#8a8bab",
  },
  [CACHE_STATUS.good]: {
    label: "Good (ready for review)",
    short: "Good",
    badge: "badge-success-soft",
    dot: "#6fc59b",
  },
  [CACHE_STATUS.bad]: {
    label: "Bad (ready for review)",
    short: "Bad",
    badge: "badge-error-soft",
    dot: "#e2758f",
  },
  [CACHE_STATUS.approved]: {
    label: "Approved",
    short: "Approved",
    badge: "badge-success",
    dot: "#3ac07c",
  },
  [CACHE_STATUS.rejected]: {
    label: "Rejected",
    short: "Rejected",
    badge: "badge-error",
    dot: "#d11044",
  },
};

/** Worst-first, which is the order a group's badge is picked in: a question with
 *  one rejected query reads as rejected, however many approved ones sit beside it.
 *
 *  A settled state outranks the proposed one it corresponds to — REJECTED is a
 *  harder fact about a question than a BAD still awaiting sign-off — and both
 *  rejections outrank the backlog, which outranks anything good: a question with an
 *  ungraded attempt still owes a review however many of its others passed.
 *  See worstStatus(). */
export const CACHE_STATUS_ORDER = [
  CACHE_STATUS.rejected,
  CACHE_STATUS.bad,
  CACHE_STATUS.pending,
  CACHE_STATUS.good,
  CACHE_STATUS.approved,
];

/**
 * The order the tabs and the KPI cards lay out in, which is NOT the badge order.
 *
 * A badge answers "how bad is this question", so it is picked worst-first. The strip
 * is a queue, and the queue reads in the order work moves through it: No feedback is
 * the backlog, then the two proposed verdicts awaiting sign-off, then the two settled
 * ones. Reversing it to match the badge would put the reviewer's actual pile last.
 *
 * Also the canonical list of "all five statuses": the tab strip, the KPI row and the
 * default selection are all built from it, so none of the three can be describing a
 * set of states the other two are not.
 */
export const CACHE_STATUS_TAB_ORDER = [
  CACHE_STATUS.pending,
  CACHE_STATUS.good,
  CACHE_STATUS.bad,
  CACHE_STATUS.approved,
  CACHE_STATUS.rejected,
];

/**
 * A cache_status value in CACHE_STATUS's spelling.
 *
 * Matched on the letters alone — spaces, case and punctuation dropped — because
 * the same state is written more than one way across the feed and the design
 * reference ("GOOD(READY FOR REVIEW)" against "GOOD(READYFORREVIEW)"). An
 * unrecognised value comes back trimmed rather than bucketed: see the note on
 * CACHE_STATUS.
 */
export function canonicalCacheStatus(raw: any): string {
  const text = (raw == null ? "" : String(raw)).trim();
  if (!text) return CACHE_STATUS.pending;
  const squashed = text.toUpperCase().replace(/[^A-Z]/g, "");
  // The two settled states, matched whole and BEFORE the prefix tests below: they
  // are single words, and leaving them to a prefix rule would let the looser test
  // answer for a value the exact one already settles.
  if (squashed === "APPROVED") return CACHE_STATUS.approved;
  if (squashed === "REJECTED") return CACHE_STATUS.rejected;
  if (squashed.startsWith("GOOD")) return CACHE_STATUS.good;
  if (squashed.startsWith("BAD")) return CACHE_STATUS.bad;
  if (squashed === "PENDING") return CACHE_STATUS.pending;
  // What the live feed writes for a message nobody has graded yet — the same
  // state the design reference spells "pending", so it is read as that rather
  // than standing up a fourth tab meaning the same thing. The value as sent is
  // still on the query (`rawStatus`) and the detail pane reports it.
  //
  // Load-bearing beyond the tabs: this is the state groupCacheRows() reads as "no
  // verdict yet", so folding it into pending is also what keeps an ungraded query
  // out of the approved and rejected counts.
  if (squashed === "NOFEEDBACK") return CACHE_STATUS.pending;
  return text;
}

/**
 * One model's share of a message, off the response's `by_model` map.
 *
 * No cost field: the response carries `cost_usd` per model, and this screen does
 * not report spend — it is a correctness review, and a figure nothing on it acts
 * on is a figure a reader has to decide to ignore. Dropped at the mapping rather
 * than hidden in the template, so there is one place the decision lives.
 */
export interface CacheModelUsage {
  /** The inference profile's last segment — the full ARN is a bedrock resource
   *  path and says nothing a reviewer reads. */
  name: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
}

/**
 * A reviewer's verdict on one cached query, spelled as the endpoint's `operation`
 * value so what the screen holds is what gets posted.
 */
export type CacheVerdict = "approve" | "reject" | "correct";

/** The verdicts posted from this browser, by message_id — see readVerdicts(). */
export type CacheVerdictStore = Record<string, CacheVerdict>;

/** What posting a verdict came back as. `ok: false` carries the message, so the
 *  panel can say why a mark did not stick instead of showing it as saved. */
export interface CacheReviewResult {
  ok: boolean;
  error: string;
}

/** One assistant message: a cached SQL query and its review state. */
export interface CacheQuery {
  message_id: string;
  /** The SQL to review, resolved by extractSql(). Empty when the message
   *  cached none — the detail pane then says so rather than showing a blank
   *  code block with a Copy button on it. */
  sql: string;
  /** The replacement SQL returned by the review feed, when this query was
   *  corrected after it was cached. */
  correctedSql: string;
  /** Canonical (CACHE_STATUS) and exactly as sent, so the footer can report the
   *  value a backend query would find. */
  status: string;
  rawStatus: string;
  ingested: boolean;
  needsClarification: boolean;
  /**
   * The SETTLED verdict standing on this query, read off `status` — APPROVED is an
   * approve and REJECTED is a reject — as one of two flags rather than a string
   * because that is how the template reads them (a chip and a button state each).
   * Never both: see how they are settled in groupCacheRows().
   *
   * A query at GOOD / BAD (READY FOR REVIEW) sets NEITHER. Those two are verdicts
   * PROPOSED and still waiting to be signed off, and reading GOOD as `approved` had
   * the pane's Approve button say "Approved", the verdict bar's headline say
   * "Approved" and the row say "1/1 approved" over a query nobody had signed off —
   * which is the distinction the five statuses exist to draw. What such a query
   * shows instead is its status, in the words CACHE_STATUS_META gives it: see the
   * component's verdictHeading(), and the muted dot on its query tab.
   */
  approved: boolean;
  rejected: boolean;
  /**
   * Who reviewed it and when, where the feed says so.
   *
   * Provenance for the detail pane, NOT what `approved` is read from: these columns
   * are not on every row the live feed sends, and a query sitting at APPROVED
   * without them is still approved. Each block in the pane is hidden when its field
   * is empty.
   */
  approvedBy: string;
  approvedAt: string;
  rejectedBy: string;
  rejectedAt: string;
  cacheUpdatedAt: string;
  feedback: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  llmTime: number;
  dbTime: number;
  s3Key: string;
  resultSummary: string;
  models: CacheModelUsage[];
  timestamp: number;
  /** The response row, kept for the Excel export so a column can be added to
   *  the export config without also having to be lifted onto this shape. */
  raw: any;
}

/** One question and every cached query answering it. */
export interface CacheGroup {
  /** Stable across reloads (conversation + prompt), so the selected item
   *  survives a refresh and a filter change. */
  questionId: string;
  conversationId: string;
  promptText: string;
  project: string;
  brandKey: string;
  teamName: string;
  schemaVersion: string;
  userId: string;
  /** The newest message in the group — what the list sorts and the date filter
   *  reads, so a question is placed by its latest activity. */
  timestamp: number;
  day: string;
  queries: CacheQuery[];
  /** Worst status across the queries (CACHE_STATUS_ORDER) — what the row is
   *  badged with while the list is unfiltered. */
  worstStatus: string;
  /**
   * The status the row is being LISTED under, which is not always the worst one.
   *
   * A question is in the Good tab when any of its queries is good, so a question
   * with a bad attempt and a good one appears there — and badging it off
   * `worstStatus` had it read "Bad (ready for review)" under a tab that says Good.
   * The row has to report the status that put it in the list.
   *
   * Set per render of the list, not per load, because it depends on the tab —
   * see the component's buildList(). Equal to `worstStatus` on the All tab.
   */
  listedStatus: string;
  /** How many of the row's queries are in `listedStatus`. Equal to the query
   *  count on the All tab, so "N of M" only ever narrows. */
  listedMatches: number;
  /** How many of the row's queries are SIGNED OFF either way — the two chips the
   *  row wears. A query still at GOOD / BAD (ready for review) counts towards
   *  neither: see CacheQuery.approved. The row's status dots stay all-inclusive,
   *  so a question with proposed verdicts on it is still visibly graded. */
  approvedCount: number;
  rejectedCount: number;
  totalTokens: number;
}

const str = (v: any): string => (v == null ? "" : String(v)).trim();
const num = (v: any): number => (v == null ? 0 : Number(v) || 0);
/** The feed writes booleans as true/false, "Y"/"N" and 1/0 depending on the
 *  column, so every flag is read through one test. */
const flag = (v: any): boolean => {
  if (v === true) return true;
  const t = str(v).toUpperCase();
  return t === "Y" || t === "TRUE" || t === "1";
};

/** Milliseconds for a message_timestamp. 0 for anything unparseable, which
 *  sorts such a row to the end rather than to 1970 via NaN. */
function parseTs(raw: any): number {
  const t = Date.parse(str(raw));
  return isNaN(t) ? 0 : t;
}

/**
 * The SQL a message cached.
 *
 * `sql_query` when the row has one. Some rows carry the query in `text` instead
 * and leave sql_query out entirely — but `text` is also where an ordinary
 * assistant reply lives (often just the prompt echoed back), so it is only taken
 * when it actually opens as a statement. Anything else answers empty, and the
 * detail pane reports "no SQL cached" rather than offering to copy prose.
 */
export function extractSql(row: any): string {
  const sql = str(row?.sql_query);
  if (sql) return sql;
  const text = str(row?.text);
  return /^\s*(with|select|insert|update|delete|create|alter|merge)\b/i.test(
    text
  )
    ? text
    : "";
}

/** Read a corrected query wherever the usage feed places it. */
export function extractCorrectedSql(row: any): string {
  return (
    str(row?.corrected_sql_query) ||
    str(row?.corrected_sql) ||
    str(row?.corrected_query) ||
    str(row?.metadata?.corrected_sql) ||
    str(row?.metadata?.corrected_query)
  );
}

/**
 * The question a message answered.
 *
 * `prompt_text` when the row carries one. The rows that do not are the ones that
 * never went through the text2sql path at all (a greeting, a schema question), and
 * on those `text` is the message itself — the same field extractSql() reads, and
 * the two are exclusive: `text` is either the SQL that was cached (in which case
 * it is the SQL, not the question) or the wording, which is the best label the row
 * has. Empty when it is neither, and the list shows "(no question text)".
 */
export function extractQuestion(row: any): string {
  const prompt = str(row?.prompt_text);
  if (prompt) return prompt;
  return extractSql(row) ? "" : str(row?.text);
}

/**
 * A field the response writes at the top level on some rows and inside
 * `metadata` on others — `project` is on both in a text2sql answer and only in
 * metadata in a plain one. Read through here so a chip is never empty for a row
 * that does carry the value, just in the other place.
 */
function eitherLevel(row: any, key: string): string {
  return str(row?.[key]) || str(row?.metadata?.[key]);
}

/** The readable tail of a bedrock inference-profile ARN. */
function modelName(arn: string): string {
  const tail = str(arn).split("/").pop() || str(arn);
  return tail || "model";
}

/** `by_model` as a list, busiest first — most tokens through it, so the model
 *  that did most of the work on a message reads at the top. */
function readModels(byModel: any): CacheModelUsage[] {
  if (!byModel || typeof byModel !== "object") return [];
  return Object.keys(byModel)
    .map((arn) => {
      const m = byModel[arn] || {};
      return {
        name: modelName(arn),
        calls: num(m.calls),
        input_tokens: num(m.input_tokens),
        output_tokens: num(m.output_tokens),
      };
    })
    .sort(
      (a, b) =>
        b.input_tokens + b.output_tokens - (a.input_tokens + a.output_tokens)
    );
}

/** One response row as a CacheQuery. */
function toQuery(row: any): CacheQuery {
  const meta = row?.metadata || {};
  return {
    message_id: str(row?.message_id),
    sql: extractSql(row),
    correctedSql: extractCorrectedSql(row),
    status: canonicalCacheStatus(row?.cache_status),
    rawStatus: str(row?.cache_status),
    ingested: flag(row?.ingested),
    needsClarification: flag(row?.needs_clarification),
    // Both settled in groupCacheRows, which is where `cache_status` and this
    // browser's own mark are weighed against each other.
    approved: false,
    rejected: false,
    approvedBy: str(row?.approved_by),
    approvedAt: str(row?.approved_at),
    rejectedBy: str(row?.rejected_by),
    rejectedAt: str(row?.rejected_at),
    cacheUpdatedAt: str(row?.cache_updated_at),
    feedback: str(row?.feedback_text),
    inputTokens: num(row?.input_tokens),
    outputTokens: num(row?.output_tokens),
    totalTokens: num(row?.total_tokens),
    llmTime: num(row?.llm_time),
    dbTime: num(row?.db_time),
    s3Key: str(meta.s3_key),
    resultSummary: str(meta.result_summary),
    models: readModels(row?.by_model),
    timestamp: parseTs(row?.message_timestamp),
    raw: row,
  };
}

/**
 * The response as the review queue: one entry per question, holding every
 * cached query that answered it.
 *
 * Grouped by conversation AND prompt, not by conversation alone: a conversation
 * is a whole session and can hold several unrelated questions, while one
 * question can be answered more than once (a retry, a corrected query) and those
 * ARE the same review item — which is what the detail pane's Query 1 / Query 2
 * tabs are for.
 *
 * `user` rows are dropped: they carry no SQL and no cache_status, so they would
 * only ever be an empty item in the list. A row with no role at all is kept —
 * the feed is the authority on what it sends, and a missing column should not
 * silently empty the screen.
 */
export function groupCacheRows(
  rows: any[],
  verdicts: CacheVerdictStore = {}
): CacheGroup[] {
  const byQuestion = new Map<string, CacheGroup>();

  (rows || []).forEach((row) => {
    const role = str(row?.role).toLowerCase();
    if (role && role !== "assistant") return;

    const conversationId = str(row?.conversation_id);
    const promptText = extractQuestion(row) || "(no question text)";
    const key = `${conversationId}::${promptText.toLowerCase()}`;
    const query = toQuery(row);
    // The SETTLED verdict on this query, from two sources.
    //
    // `cache_status` IS the verdict on this feed: APPROVED and REJECTED are the two
    // signed-off readings, GOOD / BAD (READY FOR REVIEW) are verdicts proposed and
    // still waiting on a reviewer, and NO FEEDBACK is a message nobody has graded.
    // Read off the canonical value rather than `approved_by`, which the live feed
    // does not always carry — a query sitting at APPROVED with no reviewer column
    // against it has still been approved.
    //
    // ONLY the two settled states set a flag. The ready-for-review pair used to map
    // onto approve / reject as well, on the reading that the pair differs in how
    // settled a verdict is and not in which way it went — but these flags are what
    // the Approve / Reject buttons fill, what the verdict bar's headline is read
    // from and what the list row's "N/M approved" counts, so a GOOD query rendered
    // as an approved one everywhere it mattered. A proposed verdict is exactly the
    // thing this queue exists to hand a reviewer; it is shown as its status
    // (statusLabel / verdictHeading) and leaves both buttons unfilled, so the one
    // click that signs it off is still there to make.
    //
    // The local mark wins where there is one: it is this reviewer's own POST, made
    // after the response in hand was read, and a screen that showed the older
    // status would look like the click had not worked. Where the local store says
    // nothing, the feed stands — which is how a verdict another reviewer left, or
    // one this browser has forgotten, shows up at all.
    const verdict =
      verdicts[query.message_id] ||
      (query.status === CACHE_STATUS.approved
        ? "approve"
        : query.status === CACHE_STATUS.rejected
        ? "reject"
        : "");
    query.approved = verdict === "approve";
    query.rejected = verdict === "reject";
    // Where the LOCAL mark is what settled it, the status follows the mark, because
    // the write it records is what rewrites cache_status: a query left listed as
    // Pending under an Approved chip would be this read contradicting the click that
    // preceded it, and it would be counted under the wrong tab and the wrong card
    // while the feed catches up. `rawStatus` is left alone — it reports what this
    // response actually sent, which the badge's tooltip shows, so the lag stays
    // inspectable rather than papered over.
    //
    // APPROVED / REJECTED, not the READY FOR REVIEW pair: a verdict this reviewer
    // just gave belongs in the tab named after the button they pressed, which is
    // also what the panel's hint promises. The ready-for-review greens and reds stay
    // what the FEED reports for queries nobody has graded from here — see
    // applyVerdict().
    if (verdicts[query.message_id]) {
      query.status = query.approved
        ? CACHE_STATUS.approved
        : CACHE_STATUS.rejected;
    }

    let group = byQuestion.get(key);
    if (!group) {
      group = {
        questionId: key,
        conversationId,
        promptText,
        project: eitherLevel(row, "project"),
        brandKey: eitherLevel(row, "brand_key"),
        teamName: eitherLevel(row, "team_name"),
        schemaVersion: eitherLevel(row, "schema_version"),
        userId: str(row?.user_id),
        timestamp: 0,
        day: "",
        queries: [],
        worstStatus: CACHE_STATUS.good,
        // Seeded to the worst status below, so a row is never badge-less if it is
        // rendered before buildList() has had a chance to stamp the tab's.
        listedStatus: CACHE_STATUS.good,
        listedMatches: 0,
        approvedCount: 0,
        rejectedCount: 0,
        totalTokens: 0,
      };
      byQuestion.set(key, group);
    }
    group.queries.push(query);
  });

  const groups = [...byQuestion.values()];
  groups.forEach((g) => {
    // Oldest first, so "Query 1" is the first answer the assistant gave and the
    // tab order reads as the order the reviewer would have seen them in.
    g.queries.sort((a, b) => a.timestamp - b.timestamp);
    g.timestamp = g.queries.reduce((max, q) => Math.max(max, q.timestamp), 0);
    // Sliced off the ISO string rather than read back off a Date: the feed sends
    // an explicit +00:00 offset, so a machine behind UTC would otherwise move a
    // late-evening message to the previous day and the date filter would drop it
    // from a range that visibly contains it.
    g.day = str(g.queries[g.queries.length - 1]?.raw?.message_timestamp).slice(
      0,
      10
    );
    g.approvedCount = g.queries.filter((q) => q.approved).length;
    g.rejectedCount = g.queries.filter((q) => q.rejected).length;
    g.totalTokens = g.queries.reduce((sum, q) => sum + q.totalTokens, 0);
    g.worstStatus = worstStatus(g.queries);
    // The unfiltered reading, which is what an All-tab row shows. buildList()
    // overwrites both the moment a status tab is picked.
    g.listedStatus = g.worstStatus;
    g.listedMatches = g.queries.length;
  });

  return groups;
}

/** The status a group is badged with — the worst any of its queries is in. */
export function worstStatus(queries: CacheQuery[]): string {
  const held = new Set(queries.map((q) => q.status));
  const worst = CACHE_STATUS_ORDER.find((s) => held.has(s));
  // Neither a known status nor empty: a value the backend added. Report it as
  // itself rather than as one of ours.
  return worst || queries[0]?.status || CACHE_STATUS.pending;
}

/** Where this browser's record of its own verdicts lives. See readVerdicts(). The
 *  key is unchanged from when it held approvals only — readVerdicts reads that
 *  older shape too. */
const VERDICT_STORAGE_PREFIX = "sql-cache-static-approved";

@Injectable()
export class SqlCacheStaticService {
  constructor(
    // The portal's own request plumbing — this screen's read posts through the
    // same service as every other endpoint in the app, so the client-specific
    // base URL and the session keys it resolves apply here too.
    private apiService: ApiService,
    // The chat API, where a verdict is recorded: the cache being graded is the
    // assistant's, so the write goes to the same lambda the assistant's own
    // actions do — see submitReview().
    private aiChatService: AiChatServiceService
  ) {}

  /**
   * The chat_history rows to review: the current request context, scoped to a
   * date range.
   *
   * The range is the ONE selection the backend applies — `from_date` / `to_date`
   * are what the endpoint takes, so the window a reviewer picks in the date chip
   * is a new request. Every other filter on the screen (project, team, brand,
   * schema, question text, status) is applied client-side over the one response,
   * which is what lets the tabs and KPI counts stay in step with each other
   * without a refetch per tab.
   *
   * Both dates as YYYY-MM-DD, inclusive, which is how the endpoint reads them and
   * how the component already holds them.
   *
   * A failure resolves to no rows rather than erroring, as the static screens'
   * other reads do: the screen then comes up on its empty state with the error in
   * the console, instead of on a half-built view.
   */
  loadCacheRows(
    ctx: DpmStaticContext,
    fromDate: string,
    toDate: string
  ): Observable<any[]> {
    // The context plus the window — the whole request. Everything else the screen
    // offers is applied to the response client-side.
    return this.apiService
      .getChatHistoryUsageDetail({
        // ...ctx,
        from_date: fromDate,
        to_date: toDate,
        // pageSize:100
      })
      .pipe(
        map((res: any) => this.rowsOf(res)),
        catchError((err) => {
          console.error(
            "SQL Cache Review: api/chat-history/usage/get-detail failed",
            err
          );
          return of([] as any[]);
        })
      );
  }

  /**
   * The message rows out of the response.
   *
   * `records` is what this endpoint answers with, inside an envelope that also
   * carries `total_records` and `next_token`:
   *
   *   { conversation_id, total_records, records: [...], next_token }
   *
   * The other names are kept behind it as a fallback, and a bare array (what the
   * portal's other read endpoints answer with) first of all — the rows inside are
   * what the parsing below is keyed to and those are the same either way.
   * Anything else is no rows, i.e. the empty state.
   *
   * Only the page in hand is read. `next_token` being set means the window holds
   * more than one page, and following it needs the request field the backend
   * takes it back in — so it is reported rather than guessed at: a paging
   * parameter posted under the wrong name would answer with page one again, and
   * the queue would silently repeat instead of continuing.
   */
  private rowsOf(res: any): any[] {
    if (Array.isArray(res)) return res;
    const list =
      res?.records ?? res?.data ?? res?.rows ?? res?.messages ?? res?.result;
    const rows = Array.isArray(list) ? list : [];
    const total = Number(res?.total_records);
    if (res?.next_token || (total && total > rows.length)) {
      console.warn(
        `SQL Cache Review: showing ${rows.length} of ${
          total || "?"
        } rows — the response is paged (next_token) and only the first page is read.`
      );
    }
    return rows;
  }

  // --------------------------------------------------------------- verdicts
  /**
   * Record a verdict on one cached query.
   *
   * Posted as the chat API's `cache_review` action, which is what owns the cache
   * being graded — see AiChatServiceService.submitCacheReview for the payload.
   *
   * `user_id` is the REVIEWER, off the request context (the signed-in user), not
   * `group.userId`, which is whoever asked the question. Sending the asker would
   * file every verdict against the wrong person.
   *
   * `message_timestamp` is sent exactly as the feed wrote it, not reformatted from
   * the parsed millisecond value: the backend matches on the stored string, and a
   * round trip through a Date would drop the microseconds it carries.
   *
   * `correctedSql` is the query a reviewer would rather the cache held, typed into
   * the correction box under the SQL. It rides the SAME approve call — a correction
   * is an approval with a replacement attached, not a third verdict — and is sent
   * only when there is one, so a plain Approve posts the payload it always did. It
   * is trimmed here rather than at the call site: whitespace either side of a pasted
   * query is an artefact of the paste, and an all-whitespace box is no correction.
   *
   * Never throws. A refused or unreachable write is something the panel has to
   * show as not-saved, which it can only do if it gets an answer either way.
   */
  submitReview(
    ctx: DpmStaticContext,
    group: CacheGroup,
    query: CacheQuery,
    operation: CacheVerdict,
    correctedSql?: string
  ): Observable<CacheReviewResult> {
    const reviewer = str(ctx?.user_id);
    // The two the endpoint identifies the message by. Missing either means the row
    // cannot be addressed, so the verdict is refused here rather than posted as a
    // request the backend can only reject.
    if (!query.message_id || !group.conversationId) {
      return of({
        ok: false,
        error: "This message has no conversation or message id to review against.",
      });
    }

    return this.aiChatService
      .submitCacheReview({
        conversation_id: group.conversationId,
        message_id: query.message_id,
        operation:"correct",
        user_id: reviewer,
        message_timestamp: str(query.raw?.message_timestamp) || undefined,
        corrected_sql: str(correctedSql) || undefined,
      })
      .pipe(
        map((res: any) => {
          // The lambda answers with either shape depending on the path it took, so
          // both are read; anything that is not an explicit failure is a success.
          const error = str(res?.error) || str(res?.body?.error);
          if (res?.success === false || error) {
            return { ok: false, error: error || "The review was not recorded." };
          }
          return { ok: true, error: "" };
        }),
        catchError((err) =>
          of({
            ok: false,
            error: str(err?.message) || "The review could not be sent.",
          })
        )
      );
  }

  /**
   * The verdicts posted from this browser, by message_id.
   *
   * A record of what this reviewer did, not the authority on it — the backend is,
   * and groupCacheRows() weighs the two. It exists because the read does not
   * always echo a verdict straight back: without it, a reviewer's own click would
   * disappear on the next refresh and look like it had failed.
   *
   * In a key of its own and NOT folded into the screen's filter state, because
   * opening the dashboard from its card resets the filter selection (see
   * consumeStaticDashboardEntry) and review progress must survive that — a
   * reviewer three questions into a queue has not asked to start again.
   *
   * Per dashboard so two static screens cannot read each other's marks.
   */
  readVerdicts(dashboardKey: any): CacheVerdictStore {
    try {
      const raw = localStorage.getItem(this.verdictsKey(dashboardKey));
      const parsed = raw ? JSON.parse(raw) : null;
      // An array is the shape this key held while Approve was the only verdict and
      // lived client-side. Read as approvals rather than discarded, so a reviewer
      // mid-queue does not lose their marks to the upgrade.
      if (Array.isArray(parsed)) {
        const store: CacheVerdictStore = {};
        parsed.forEach((id: any) => {
          if (typeof id === "string" && id) store[id] = "approve";
        });
        return store;
      }
      if (!parsed || typeof parsed !== "object") return {};
      const store: CacheVerdictStore = {};
      Object.keys(parsed).forEach((id) => {
        const v = parsed[id];
        if (v === "approve" || v === "reject") store[id] = v;
      });
      return store;
    } catch {
      // Storage unavailable (private mode / quota) or a corrupt entry: nothing is
      // marked, which is the honest reading of "we cannot tell".
      return {};
    }
  }

  writeVerdicts(dashboardKey: any, verdicts: CacheVerdictStore): void {
    try {
      localStorage.setItem(
        this.verdictsKey(dashboardKey),
        JSON.stringify(verdicts)
      );
    } catch {
      /* storage unavailable — the mark stands for this visit only */
    }
  }

  private verdictsKey(dashboardKey: any): string {
    return `${VERDICT_STORAGE_PREFIX}:${dashboardKey || "default"}`;
  }
}
